import React, { useState, useRef, useEffect } from 'react';
import {
  ScheduleTimeSlotWindowDialog,
  type ScheduleTimeSlotSaveData,
} from './schedule-time-slot-window-dialog.tsx';
import {
  ScheduleTimeSlotWindow,
  type ScheduleTimeSlotWindow as TimeSlotWindowType,
} from './schedule-time-slot-window.tsx';
import { POWER_SETPOINT_COLORS, type PowerSetpointType } from './schedule-time-slot-colors.ts';
import { cn } from '@/utils/utils.ts';
import {
  SCHEDULE_DAY_COLUMN_WIDTH_PX,
  SCHEDULE_SNAP_MINUTES,
  SCHEDULE_MIN_DURATION_MINUTES,
  SCHEDULE_TIME_SLOT_WINDOW_DAY_COLUMN_OFFSET_PX,
  SCHEDULE_HOURS_IN_DAY,
  SCHEDULE_MINUTES_IN_HOUR,
  SCHEDULE_DEFAULT_DURATION_MINUTES,
  SCHEDULE_HOUR_COLUMN_WIDTH_PX,
} from '@/features/plants/components/plant-schedule/schedule/constants.ts';
import { ScheduleHoursHeader } from './schedule-hours-header.tsx';
import { ScheduleDaysColumn } from './schedule-days-column.tsx';
import { ScheduleHoursGrid } from './schedule-hours-grid.tsx';
import { ScheduleTimeCursor } from './schedule-time-cursor.tsx';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  type ScheduleTimeSlot,
  type WeeklySchedule,
  type Weekday,
  WEEKDAYS,
} from '@/features/plants/schemas/schedule-plant.schema.ts';
import { useWeeklyScheduleActions } from '../hooks/use-weekly-schedule-actions.ts';
import {
  createHoursLabels,
  calculateTimeSlotPositions,
  convertPixelsToMinutes,
  getTotalScheduleWidth,
} from './weekly-schedule-editor.utils.tsx';

interface DialogState {
  isOpen: boolean;
  mode?: 'add' | 'edit';
  day?: Weekday;
  initialTimeSlotData?: ScheduleTimeSlot;
}

type DragMode = 'move' | 'resize-left' | 'resize-right';
interface DragState {
  mode: DragMode;
  timeSlot: TimeSlotWindowType;
  startClientX: number;
  originalStartMinutes: number;
  originalEndMinutes: number;
  previewStartMinutes: number;
  previewEndMinutes: number;
  isDragging: boolean;
  didMove: boolean;
}

const LEGEND_ITEMS: PowerSetpointType[] = ['charging', 'discharging', 'idle'];

function ScheduleLegend() {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-2">
      <div className="flex items-center gap-4">
        {LEGEND_ITEMS.map((type) => {
          const colors = POWER_SETPOINT_COLORS[type];
          return (
            <div key={type} className="flex items-center gap-1.5">
              <div className={cn('h-3 w-3 rounded-sm', colors.dot)} />
              <span className="text-sm text-slate-600">{colors.label}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 border-l border-slate-200 pl-5">
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-sm border border-dashed border-slate-400 bg-slate-200 opacity-50" />
          <span className="text-sm text-slate-600">Inactive</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="h-3 w-3 rounded-sm border border-dashed border-slate-400"
            style={{
              backgroundImage:
                'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.25) 2px, rgba(0,0,0,0.25) 3px)',
            }}
          />
          <span className="text-sm text-slate-600">Expired</span>
        </div>
      </div>
    </div>
  );
}

export const WeeklyScheduleEditor = ({
  weeklySchedule,
  onChangeWeeklySchedule,
  className,
}: {
  weeklySchedule: WeeklySchedule;
  onChangeWeeklySchedule: (weeklySchedule: WeeklySchedule) => void;
  className?: string;
}) => {
  const DAY_COLUMN_WIDTH = SCHEDULE_DAY_COLUMN_WIDTH_PX;

  const [timeSlots, setTimeSlots] = useState<TimeSlotWindowType[]>([]);

  const [drag, setDrag] = useState<DragState | null>(null);
  const [suppressTimeSlotClick, setSuppressTimeSlotClick] = useState(false);

  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
  });

  const gridRef = useRef<HTMLDivElement>(null);
  const dayRefs = useRef<{ [key in Weekday]?: HTMLDivElement | null }>({});
  const hoursRefs = useRef<{ [key in Weekday]?: HTMLDivElement | null }>({});
  const mouseMoveHandlerRef = useRef<((ev: MouseEvent) => void) | null>(null);
  const mouseUpHandlerRef = useRef<((ev: MouseEvent) => void) | null>(null);

  const [scheduleWidth, setScheduleWidth] = useState(0);

  const hours = createHoursLabels();

  const {
    checkTimeSlotOverlap,
    updateTimeSlotTimes,
    saveTimeSlot,
    addTimeSlot,
    deleteTimeSlot,
    findAvailableTimeWindow,
    convertTimeToMinutes,
    convertMinutesToTimeString,
  } = useWeeklyScheduleActions(weeklySchedule, onChangeWeeklySchedule);

  useEffect(() => {
    const updateTimeSlotPositions = () => {
      const timeSlotWindows = calculateTimeSlotPositions({
        weeklySchedule,
        gridEl: gridRef.current,
        dayRefs,
        hoursRefs,
      });
      setTimeSlots(timeSlotWindows);
      setScheduleWidth(getTotalScheduleWidth(hoursRefs));
    };

    updateTimeSlotPositions();

    const handleResize = () => {
      updateTimeSlotPositions();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [weeklySchedule]);

  const cleanupMouseListeners = () => {
    if (mouseMoveHandlerRef.current) {
      window.removeEventListener('mousemove', mouseMoveHandlerRef.current);
      mouseMoveHandlerRef.current = null;
    }
    if (mouseUpHandlerRef.current) {
      window.removeEventListener('mouseup', mouseUpHandlerRef.current);
      mouseUpHandlerRef.current = null;
    }
  };

  useEffect(() => {
    return cleanupMouseListeners;
  }, []);

  const getScheduleWidth = (): number => {
    return scheduleWidth;
  };

  const calculateDragPositions = (
    mode: DragMode,
    originalStartMinutes: number,
    originalEndMinutes: number,
    minutesDelta: number
  ) => {
    const duration = originalEndMinutes - originalStartMinutes;
    let newStart = originalStartMinutes;
    let newEnd = originalEndMinutes;

    if (mode === 'move') {
      newStart = originalStartMinutes + minutesDelta;
      newEnd = originalEndMinutes + minutesDelta;

      if (newStart < 0) {
        newStart = 0;
        newEnd = duration;
      }
      if (newEnd > SCHEDULE_HOURS_IN_DAY * SCHEDULE_MINUTES_IN_HOUR) {
        newEnd = SCHEDULE_HOURS_IN_DAY * SCHEDULE_MINUTES_IN_HOUR;
        newStart = newEnd - duration;
      }
    } else if (mode === 'resize-left') {
      newStart = originalStartMinutes + minutesDelta;
      const maxStart = originalEndMinutes - SCHEDULE_MIN_DURATION_MINUTES;
      newStart = Math.max(0, Math.min(maxStart, newStart));
    } else if (mode === 'resize-right') {
      newEnd = originalEndMinutes + minutesDelta;
      const minEnd = originalStartMinutes + SCHEDULE_MIN_DURATION_MINUTES;
      newEnd = Math.max(
        minEnd,
        Math.min(SCHEDULE_HOURS_IN_DAY * SCHEDULE_MINUTES_IN_HOUR, newEnd)
      );
    }

    return {
      start: Math.round(newStart / SCHEDULE_SNAP_MINUTES) * SCHEDULE_SNAP_MINUTES,
      end: Math.round(newEnd / SCHEDULE_SNAP_MINUTES) * SCHEDULE_SNAP_MINUTES,
    };
  };

  const completeDrag = (dragState: DragState) => {
    if (!dragState.didMove) return;

    setSuppressTimeSlotClick(true);
    requestAnimationFrame(() => setSuppressTimeSlotClick(false));

    const startTime = convertMinutesToTimeString(dragState.previewStartMinutes);
    const endTime = convertMinutesToTimeString(dragState.previewEndMinutes);

    const validation = checkTimeSlotOverlap(
      dragState.timeSlot.day,
      startTime,
      endTime,
      dragState.timeSlot.slot.id
    );

    if (!validation.isValid && validation.reason === 'complete-overlap') {
      const startTimeSlotTime = validation.conflictingSlots?.find(
        ({ reason }) => reason === 'start-time-overlap'
      )?.slot;

      const endTimeSlotTime = validation.conflictingSlots?.find(
        ({ reason }) => reason === 'end-time-overlap'
      )?.slot;

      if (!startTimeSlotTime?.endTime || !endTimeSlotTime?.startTime) return;

      return updateTimeSlotTimes(
        dragState.timeSlot.day,
        dragState.timeSlot.slot.id,
        startTimeSlotTime?.endTime,
        endTimeSlotTime?.startTime
      );
    }

    if (!validation.isValid && validation.reason === 'start-time-overlap') {
      const slotTime = validation.conflictingSlots?.find(
        ({ reason }) => reason === 'start-time-overlap'
      )?.slot;

      if (!slotTime?.endTime) return;

      return updateTimeSlotTimes(
        dragState.timeSlot.day,
        dragState.timeSlot.slot.id,
        slotTime?.endTime,
        endTime
      );
    }

    if (!validation.isValid && validation.reason === 'end-time-overlap') {
      const slotTime = validation.conflictingSlots?.find(
        ({ reason }) => reason === 'end-time-overlap'
      )?.slot;

      if (!slotTime?.endTime) return;

      return updateTimeSlotTimes(
        dragState.timeSlot.day,
        dragState.timeSlot.slot.id,
        startTime,
        slotTime.startTime
      );
    }

    if (validation.isValid && dragState.timeSlot.day) {
      return updateTimeSlotTimes(
        dragState.timeSlot.day,
        dragState.timeSlot.slot.id,
        startTime,
        endTime
      );
    }
  };

  const calculateDragTimeSlot = (
    timeSlot: TimeSlotWindowType,
    dragState: DragState
  ): TimeSlotWindowType => {
    const scheduleWidth = getScheduleWidth();
    const leftPx =
      (dragState.previewStartMinutes / (SCHEDULE_HOURS_IN_DAY * SCHEDULE_MINUTES_IN_HOUR)) *
      scheduleWidth;
    const widthPx =
      ((dragState.previewEndMinutes - dragState.previewStartMinutes) /
        (SCHEDULE_HOURS_IN_DAY * SCHEDULE_MINUTES_IN_HOUR)) *
      scheduleWidth;

    return {
      ...timeSlot,
      left: leftPx,
      width: widthPx,
      slot: {
        ...timeSlot.slot,
        startTime: convertMinutesToTimeString(dragState.previewStartMinutes),
        endTime: convertMinutesToTimeString(dragState.previewEndMinutes),
      },
    };
  };

  const getEffectiveTimeSlot = (timeSlot: TimeSlotWindowType): TimeSlotWindowType => {
    if (
      !drag?.isDragging ||
      drag.timeSlot.day !== timeSlot.day ||
      drag.timeSlot.slot.id !== timeSlot.slot.id
    ) {
      return timeSlot;
    }

    return calculateDragTimeSlot(timeSlot, drag);
  };

  const startDragGeneric = (
    mode: DragMode,
    timeSlot: TimeSlotWindowType,
    e: React.MouseEvent
  ) => {
    e.preventDefault();

    const originalStartMinutes = convertTimeToMinutes(timeSlot.slot.startTime);
    const originalEndMinutes = convertTimeToMinutes(timeSlot.slot.endTime);

    setDrag({
      mode,
      timeSlot,
      startClientX: e.clientX,
      originalStartMinutes,
      originalEndMinutes,
      previewStartMinutes: originalStartMinutes,
      previewEndMinutes: originalEndMinutes,
      isDragging: true,
      didMove: false,
    });

    cleanupMouseListeners();

    const onMouseMove = (ev: MouseEvent) => {
      setDrag((prev) => {
        if (!prev) return prev;

        const deltaX = ev.clientX - prev.startClientX;
        const minutesDelta = convertPixelsToMinutes(deltaX, hoursRefs, SCHEDULE_SNAP_MINUTES);

        const { start, end } = calculateDragPositions(
          prev.mode,
          prev.originalStartMinutes,
          prev.originalEndMinutes,
          minutesDelta
        );

        return {
          ...prev,
          previewStartMinutes: start,
          previewEndMinutes: end,
          didMove: prev.didMove || minutesDelta !== 0,
        };
      });
    };

    const onMouseUp = () => {
      cleanupMouseListeners();

      setDrag((prev) => {
        if (!prev) return null;
        completeDrag(prev);
        return null;
      });
    };

    window.addEventListener('mousemove', onMouseMove);
    mouseMoveHandlerRef.current = onMouseMove;
    window.addEventListener('mouseup', onMouseUp, { once: true });
    mouseUpHandlerRef.current = onMouseUp;
  };

  const openAddTimeSlotDialog = (
    day: Weekday,
    initialTimes?: { startTime: string; endTime: string }
  ) => {
    if (!initialTimes?.startTime || !initialTimes?.endTime) {
      return setDialogState({
        isOpen: true,
        mode: 'add',
        day,
        initialTimeSlotData: undefined,
      });
    }

    const { startTime, endTime } = initialTimes;
    const { isValid } = checkTimeSlotOverlap(day, startTime, endTime);
    if (!isValid) return;

    setDialogState({
      isOpen: true,
      mode: 'add',
      day,
      initialTimeSlotData: {
        id: generateTimeSlotId(startTime, endTime),
        startTime: startTime,
        endTime: endTime,
        powerSetpoint: 0,
        isActive: true,
        validUntil: null,
      },
    });
  };

  const openEditTimeSlotDialog = (day: Weekday, timeSlot: ScheduleTimeSlot) => {
    setDialogState({
      isOpen: true,
      mode: 'edit',
      day,
      initialTimeSlotData: timeSlot,
    });
  };

  const closeTimeSlotDialog = () => {
    setDialogState({
      isOpen: false,
    });
  };

  const handleDayClick = (day: Weekday) => {
    openAddTimeSlotDialog(day);
  };

  const handleHoursClick = (day: Weekday, e: React.MouseEvent) => {
    const hoursEl = hoursRefs.current[day];
    if (!hoursEl) return;
    const rect = hoursEl.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const totalWidth = rect.width;
    if (totalWidth <= 0) return;

    const minutes =
      Math.round(((x / totalWidth) * SCHEDULE_HOURS_IN_DAY * SCHEDULE_MINUTES_IN_HOUR) / 5) *
      5;

    const { startMinutes, endMinutes } = findAvailableTimeWindow(
      day,
      minutes,
      SCHEDULE_DEFAULT_DURATION_MINUTES
    );

    const startTime = convertMinutesToTimeString(startMinutes);
    const endTime = convertMinutesToTimeString(endMinutes);

    openAddTimeSlotDialog(day, { startTime, endTime });
  };

  const handleTimeSlotClick = (
    day: Weekday,
    timeSlot: ScheduleTimeSlot,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    openEditTimeSlotDialog(day, timeSlot);
  };

  const handleSave = (data: ScheduleTimeSlotSaveData) => {
    const { startTime, endTime, powerSetpoint, isActive, validUntil } = data;

    const validation = checkTimeSlotOverlap(
      dialogState.day || null,
      startTime,
      endTime,
      dialogState.mode === 'edit'
        ? dialogState.initialTimeSlotData?.id
        : generateTimeSlotId(startTime, endTime)
    );

    if (!validation.isValid || !dialogState.day) {
      return;
    }

    if (dialogState.mode === 'edit' && dialogState.initialTimeSlotData) {
      saveTimeSlot(dialogState.day, {
        ...dialogState.initialTimeSlotData,
        startTime,
        endTime,
        powerSetpoint,
        isActive,
        validUntil,
      });

      closeTimeSlotDialog();
      return;
    }

    const newTimeSlot: ScheduleTimeSlot = {
      id: generateTimeSlotId(startTime, endTime),
      startTime,
      endTime,
      powerSetpoint,
      isActive,
      validUntil,
    };

    addTimeSlot(dialogState.day, newTimeSlot);

    closeTimeSlotDialog();
  };

  const handleDelete = () => {
    if (dialogState.mode === 'edit' && dialogState.initialTimeSlotData && dialogState.day) {
      deleteTimeSlot(dialogState.day, dialogState.initialTimeSlotData.id);
    }

    closeTimeSlotDialog();
  };

  return (
    <div className={cn('ml-1 flex w-full flex-col gap-2', className)}>
      <ScrollArea className="max-w-full">
        <div className="relative w-min" ref={gridRef}>
          <div className="">
            <div className="relative">
              <div className="flex">
                <div className="sticky left-0 z-20">
                  <div className="h-8 bg-white"></div>
                  <ScheduleDaysColumn
                    days={[...WEEKDAYS]}
                    onDayClick={handleDayClick}
                    onDayRef={(day, el) => {
                      if (el) dayRefs.current[day] = el;
                    }}
                    dayColumnWidthPx={DAY_COLUMN_WIDTH}
                  />
                </div>
                <div className="">
                  <div className="">
                    <ScheduleHoursHeader
                      hours={hours}
                      hourColumnWidthPx={SCHEDULE_HOUR_COLUMN_WIDTH_PX}
                    />
                    <ScheduleHoursGrid
                      days={[...WEEKDAYS]}
                      hours={hours}
                      onHoursRef={(day, el) => {
                        if (el) hoursRefs.current[day] = el;
                      }}
                      onHoursClick={handleHoursClick}
                      hourColumnWidthPx={SCHEDULE_HOUR_COLUMN_WIDTH_PX}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {timeSlots.map((timeSlot) => (
            <ScheduleTimeSlotWindow
              key={`${timeSlot.day}-${timeSlot.slot.id}`}
              timeSlot={getEffectiveTimeSlot(timeSlot)}
              onTimeSlotClick={handleTimeSlotClick}
              onStartMove={(timeSlot, event) => startDragGeneric('move', timeSlot, event)}
              onStartResizeLeft={(timeSlot, event) =>
                startDragGeneric('resize-left', timeSlot, event)
              }
              onStartResizeRight={(timeSlot, event) =>
                startDragGeneric('resize-right', timeSlot, event)
              }
              dayColumnWidthPx={
                DAY_COLUMN_WIDTH + SCHEDULE_TIME_SLOT_WINDOW_DAY_COLUMN_OFFSET_PX
              }
              suppressClicks={!!drag?.isDragging || suppressTimeSlotClick}
            />
          ))}

          <ScheduleTimeCursor
            gridRef={gridRef}
            dayRefs={dayRefs}
            scheduleWidth={scheduleWidth}
            dayColumnWidthPx={
              DAY_COLUMN_WIDTH + SCHEDULE_TIME_SLOT_WINDOW_DAY_COLUMN_OFFSET_PX
            }
          />
        </div>
        <ScrollBar orientation="horizontal" className="z-40 h-3" />
      </ScrollArea>
      <div className="flex w-full justify-center">
        <ScheduleLegend />
      </div>

      <ScheduleTimeSlotWindowDialog
        key={dialogState.initialTimeSlotData?.id}
        isOpen={dialogState.isOpen}
        mode={dialogState.mode}
        day={dialogState.day}
        initialData={dialogState.initialTimeSlotData || undefined}
        onClose={closeTimeSlotDialog}
        onSave={handleSave}
        onDelete={dialogState.mode === 'edit' ? handleDelete : undefined}
        validateTimeSlotWindow={(startTime, endTime) =>
          checkTimeSlotOverlap(
            dialogState.day || null,
            startTime,
            endTime,
            dialogState.mode === 'edit'
              ? dialogState.initialTimeSlotData?.id
              : generateTimeSlotId(startTime, endTime)
          )
        }
      />
    </div>
  );
};

function generateTimeSlotId(startTime: string, endTime: string) {
  return `${startTime}-${endTime}`;
}
